'use client';

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useInternalStepValidation } from '../utils/useInternalStepValidation';
import { InternalStepValidationResult } from '../utils/useInternalStepValidation';

type InternalStepValidationContextType = {
  // Validate a specific internal step
  validateStep: (stepIndex: number) => InternalStepValidationResult;
  // Check if a step is valid
  isStepValid: (stepIndex: number) => boolean;
  // Get errors for a specific step
  getStepErrors: (stepIndex: number) => Record<string, string>;
  // Get missing fields for a specific step
  getMissingFields: (stepIndex: number) => string[];
  // Track visited steps
  markStepVisited: (stepIndex: number) => void;
  // Check if a step has been visited
  isStepVisited: (stepIndex: number) => boolean;
  // Reset visited steps (for start over)
  resetVisitedSteps: () => void;
  // Main step number
  mainStep: number;
  // Total number of internal steps
  totalSteps: number;
  // Whether addon step exists (for PropertyStep dynamic steps)
  hasAddonStep?: boolean;
};

const InternalStepValidationContext =
  createContext<InternalStepValidationContextType | undefined>(undefined);

export { InternalStepValidationContext };

type InternalStepValidationProviderProps = {
  children: ReactNode;
  mainStep: number;
  totalSteps: number;
  hasAddonStep?: boolean;
  hasFilterStep?: boolean; // For ServicesStep: whether filter form step exists
};

export function InternalStepValidationProvider({
  children,
  mainStep,
  totalSteps,
  hasAddonStep = false,
  hasFilterStep = true, // Default to true for backward compatibility
}: InternalStepValidationProviderProps) {
  // Track visited steps
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set());

  // Mark a step as visited
  const markStepVisited = useCallback((stepIndex: number) => {
    setVisitedSteps((prev) => {
      const newSet = new Set(prev);
      newSet.add(stepIndex);
      return newSet;
    });
  }, []);

  // Check if a step has been visited
  const isStepVisited = useCallback(
    (stepIndex: number): boolean => {
      return visitedSteps.has(stepIndex);
    },
    [visitedSteps]
  );

  // Reset visited steps (for start over)
  const resetVisitedSteps = useCallback(() => {
    setVisitedSteps(new Set());
  }, []);

  // Validate all steps at component level (hooks must be called at top level)
  const step0Validation = useInternalStepValidation(mainStep, 0, hasAddonStep);
  const step1Validation = useInternalStepValidation(mainStep, 1, hasAddonStep);
  const step2Validation = useInternalStepValidation(mainStep, 2, hasAddonStep);
  const step3Validation = useInternalStepValidation(mainStep, 3, hasAddonStep);
  const step4Validation = useInternalStepValidation(mainStep, 4, hasAddonStep);

  // Map of step validations for easy lookup
  const stepValidations = useMemo(() => ({
    0: step0Validation,
    1: step1Validation,
    2: step2Validation,
    3: step3Validation,
    4: step4Validation,
  }), [step0Validation, step1Validation, step2Validation, step3Validation, step4Validation]);

  // Map array index to logical validation case
  // For PropertyStep and ServicesStep with dynamic steps, this handles the mapping
  const mapStepIndexToLogicalCase = useCallback(
    (stepIndex: number): number => {
      // For ServicesStep (mainStep === 1), handle dynamic step mapping
      if (mainStep === 1) {
        if (!hasFilterStep) {
          // No filter step: Step 0 = Select Primary Service (case 1), Step 1 = Contact (case 2)
          if (stepIndex === 0) return 1; // Map to Select Primary Service
          if (stepIndex === 1) return 2; // Map to Contact Form
          return stepIndex;
        } else {
          // Has filter step: Step 0 = Filter (case 0), Step 1 = Select Primary (case 1), Step 2 = Contact (case 2)
          return stepIndex; // Direct mapping
        }
      }
      
      // For PropertyStep (mainStep === 2), handle dynamic step mapping
      if (mainStep === 2) {
        // Case 0: Find Property (always index 0)
        if (stepIndex === 0) return 0;
        // Case 1: Review Property Details (always index 1)
        if (stepIndex === 1) return 1;
        // Case 2: Addon Recommendations (if exists) OR More Information (if no addons)
        if (stepIndex === 2) return 2;
        // Case 3: More Information (only if addons step exists)
        // If stepIndex 3 but no addons, this shouldn't happen, but return case 2 as fallback
        if (stepIndex === 3) {
          return hasAddonStep ? 3 : 2;
        }
        // For any other index, return as-is (will be handled by default case)
        return stepIndex;
      }
      // For other steps, array index equals logical case
      return stepIndex;
    },
    [mainStep, hasAddonStep, hasFilterStep]
  );

  // Validate a specific internal step
  const validateStep = useCallback(
    (stepIndex: number): InternalStepValidationResult => {
      // Map array index to logical case
      const logicalCase = mapStepIndexToLogicalCase(stepIndex);
      // Get validation from the map
      const validation = stepValidations[logicalCase as keyof typeof stepValidations];
      return validation || { isValid: true, missingFields: [], errors: {} };
    },
    [stepValidations, mapStepIndexToLogicalCase]
  );

  // Check if a step is valid
  const isStepValid = useCallback(
    (stepIndex: number): boolean => {
      return validateStep(stepIndex).isValid;
    },
    [validateStep]
  );

  // Get errors for a specific step
  const getStepErrors = useCallback(
    (stepIndex: number): Record<string, string> => {
      return validateStep(stepIndex).errors;
    },
    [validateStep]
  );

  // Get missing fields for a specific step
  const getMissingFields = useCallback(
    (stepIndex: number): string[] => {
      return validateStep(stepIndex).missingFields;
    },
    [validateStep]
  );

  const value = useMemo(
    () => ({
      validateStep,
      isStepValid,
      getStepErrors,
      getMissingFields,
      markStepVisited,
      isStepVisited,
      resetVisitedSteps,
      mainStep,
      totalSteps,
      hasAddonStep,
    }),
    [
      validateStep,
      isStepValid,
      getStepErrors,
      getMissingFields,
      markStepVisited,
      isStepVisited,
      resetVisitedSteps,
      mainStep,
      totalSteps,
      hasAddonStep,
      hasFilterStep,
    ]
  );

  return (
    <InternalStepValidationContext.Provider value={value}>
      {children}
    </InternalStepValidationContext.Provider>
  );
}

export function useInternalStepValidationContext() {
  const context = useContext(InternalStepValidationContext);
  if (!context) {
    throw new Error(
      'useInternalStepValidationContext must be used within InternalStepValidationProvider'
    );
  }
  return context;
}

